<script lang="ts">
    import { trackButtonClick } from '$lib/global/tracking';
    import { getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import get from 'lodash/get';
    import { onMount } from 'svelte';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { page } from '$app/stores';

    // Types
    import type { ComponentType } from 'svelte';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { Language } from '$lib/types/Language';
    import type { ButtonContent } from '$lib/types/Components/Button/ButtonContent';

    export let id = '';
    export let content: ButtonContent;
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'de';

    const {
        data: { pageIdSlugMap },
    } = $page;

    let Button: ComponentType;

    const personalizedText = personalizeText(
        content.wysiwyg,
        trackingProperties,
        campaignId,
        language,
    );
    const regularNextPage = get(content, 'linking.pageId', nextPage);
    const externalLink = get(content, 'linking.url', '');
    const bgColor = getColor(content.backgroundColor, theme.buttonBackgroundColor);
    const color = getColor(content.color, getColorByLightness(bgColor));

    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    onMount(async () => {
        Button = (await import('$lib/components/_DisplayComponents/Button.svelte')).default;
    });

    const handleClick = async () => {
        // Tracking
        trackButtonClick(id, content);

        // Navigate to external page
        if (!content.linking.isInternal && externalLink) {
            return nextPageNavigator.goExternal(externalLink);
        }

        return nextPageNavigator.go(regularNextPage);
    };

    const handleMouseEnter = () => {
        nextPageNavigator.preload(regularNextPage, !content.linking.isInternal && !!externalLink);
    };
</script>

<svelte:component
    this={Button}
    draft={personalizedText}
    box={content.box}
    size={content.size}
    align={content.align}
    onClick={handleClick}
    onMouseEnter={handleMouseEnter}
    animation={content.animation}
    {transitionDelay}
    {transitionEffect}
    {bgColor}
    {color}
    {id}
/>
